import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { SimulateDisplayRequestFormSchemaValues } from 'src/features/actions/api/useSimulateDisplayRequest';
import { DISPLAY_ACTION_TYPE_MAP, DISPLAY_IDLE_ACTION_TYPE_MAP } from 'src/features/actions/utils/constants';
import { actionsMessages } from 'src/features/actions/utils/messages';
import { DISPLAY_ACTION, DISPLAY_IDLE_ACTION } from 'src/features/actions/utils/types';
import { identifierTypeTranslationMap } from 'src/features/eventLog/utils/constants';
import { useListSystemComponents } from 'src/features/systems/api/systemComponents/useListSystemComponents';
import { displaySettingsMessages } from 'src/features/systems/utils/displaySettingsMessages';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { SYSTEM_COMPONENT } from 'src/features/systems/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type SimulateDisplayRequestFormProps = {
    form: UseFormReturn<SimulateDisplayRequestFormSchemaValues>;
    onSubmit: () => void;
};

function SimulateDisplayRequestForm({ onSubmit, form }: SimulateDisplayRequestFormProps) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { data: componentsData } = useListSystemComponents({
        parkingId,
        component: SYSTEM_COMPONENT.GATEWAY_DISPLAY,
    });

    const { action, idle_action } = form.getValues();

    const componentOptions = React.useMemo(() => {
        if (!componentsData) return [];
        return componentsData.results.map(({ name, id }) => {
            return { value: id, label: name };
        });
    }, [componentsData]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SelectFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="componentId"
                label={formatMessage(systemsMessages.component)}
                options={componentOptions}
            />
            <SelectFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="action"
                label={formatMessage(actionsMessages.action)}
                options={Object.values(DISPLAY_ACTION)}
                optionTranslationMap={DISPLAY_ACTION_TYPE_MAP}
                required={false}
                disabled={idle_action !== undefined}
            />
            <SelectFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="idle_action"
                label={formatMessage(actionsMessages.idleState)}
                options={Object.values(DISPLAY_IDLE_ACTION)}
                optionTranslationMap={DISPLAY_IDLE_ACTION_TYPE_MAP}
                required={false}
                disabled={action !== undefined}
            />
            <InputFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="license_plate"
                label={formatMessage(identifierTypeTranslationMap.license_plate)}
                required={false}
            />
            <InputFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="title"
                label={formatMessage(displaySettingsMessages.title)}
                required={false}
            />
            <InputFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="subtitle"
                label={formatMessage(displaySettingsMessages.subtitle)}
                required={false}
            />
            <InputFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="setting_json_placeholders.reason"
                label={formatMessage(actionsMessages.reason)}
                required={false}
            />
            <InputFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="setting_json_placeholders.free_spaces"
                label={formatMessage(actionsMessages.freeSpaces)}
                required={false}
                type="number"
                min={0}
            />
            <FormField
                control={form.control}
                name="setting_json_placeholders.total_remaining_amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...actionsMessages.totalRemainingAmount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(actionsMessages.totalRemainingAmount)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        if (inputValue === '' || /^-?\d{0,20}(?:\.\d{0,4})?$/.test(inputValue)) {
                                            field.onChange(inputValue);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="setting_json_placeholders.amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...actionsMessages.amount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    placeholder={`${formatMessage(actionsMessages.amount)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        field.onChange(v === '' ? undefined : Number(v));
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="setting_json_placeholders.amount_paid"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...actionsMessages.amountPaid} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    placeholder={`${formatMessage(actionsMessages.amountPaid)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        field.onChange(v === '' ? undefined : Number(v));
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <InputFormField<SimulateDisplayRequestFormSchemaValues>
                form={form}
                name="setting_json_placeholders.currency"
                label={formatMessage(actionsMessages.currency)}
                required={false}
            />
        </Form>
    );
}

export default SimulateDisplayRequestForm;
